import { getCanvasItemBounds, getCanvasWarnings } from './canvas';
import { getPatternMetrics } from './config';
import type { CanvasDocument, CanvasItem, CanvasRotation } from './types';

export const RESOLUME_PROFILE = {
  application: 'Resolume Arena',
  majorVersion: 7,
  minorVersion: 15,
  microVersion: 0,
  revision: 23381,
  label: 'Arena 7.15+',
} as const;

export interface ResolumePoint {
  x: number;
  y: number;
}

export interface ResolumeLayout {
  format: 'screengrid-resolume-layout';
  formatVersion: 2;
  compatibility: {
    application: typeof RESOLUME_PROFILE.application;
    profile: typeof RESOLUME_PROFILE.label;
    presetFormat: 'Advanced Output XML';
  };
  composition: {
    name: string;
    width: number;
    height: number;
  };
  screens: Array<{
    index: number;
    id: string;
    name: string;
    virtualOutput: {
      width: number;
      height: number;
    };
    slices: Array<{
      index: number;
      id: string;
      itemId: string;
      name: string;
      type: CanvasItem['kind'];
      mediaFile: string | null;
      nativeResolution: {
        width: number;
        height: number;
      };
      inputRotation: CanvasRotation;
      inputBounds: { x: number; y: number; width: number; height: number };
      inputQuad: ResolumePoint[];
      outputQuad: ResolumePoint[];
    }>;
  }>;
}

export function buildResolumeLayout(
  canvas: CanvasDocument,
  mediaFiles: string[] = [],
): ResolumeLayout {
  validateResolumeCanvas(canvas);

  const slices = canvas.items.map((item, index) => {
    const { resolution } = getPatternMetrics(item.config);
    const inputQuad = getResolumeInputQuad(item);
    return {
      index: index + 1,
      id: `slice-${index + 1}`,
      itemId: item.id,
      name: item.name,
      type: item.kind,
      mediaFile: mediaFiles[index] ?? null,
      nativeResolution: { ...resolution },
      inputRotation: item.rotation,
      inputBounds: getCanvasItemBounds(item),
      inputQuad,
      outputQuad: inputQuad.map((point) => ({ ...point })),
    };
  });

  return {
    format: 'screengrid-resolume-layout',
    formatVersion: 2,
    compatibility: {
      application: RESOLUME_PROFILE.application,
      profile: RESOLUME_PROFILE.label,
      presetFormat: 'Advanced Output XML',
    },
    composition: {
      name: canvas.title,
      width: canvas.resolution.width,
      height: canvas.resolution.height,
    },
    screens: [
      {
        index: 1,
        id: 'screen-1',
        name: canvas.title.trim() || 'ScreenGrid Canvas',
        virtualOutput: { ...canvas.resolution },
        slices,
      },
    ],
  };
}

export function buildResolumePresetXml(layout: ResolumeLayout): string {
  const screens = layout.screens
    .map((screen, screenIndex) => {
      const screenUniqueId = 1_900_000_000_000 + screenIndex * 1000;
      const outputName = screen.name || `Screen ${screenIndex + 1}`;
      const slices = screen.slices
        .map((slice, sliceIndex) => buildSliceXml(slice, screenUniqueId + sliceIndex + 1))
        .join('\n');
      return [
        `\t\t\t<Screen name="${escapeXml(outputName)}" uniqueId="${screenUniqueId}">`,
        '\t\t\t\t<Params name="Params">',
        `\t\t\t\t\t<Param name="Name" T="STRING" default="" value="${escapeXml(outputName)}"/>`,
        '\t\t\t\t\t<Param name="Enabled" T="BOOL" default="1" value="1"/>',
        '\t\t\t\t\t<Param name="Hidden" T="BOOL" default="0" value="0"/>',
        '\t\t\t\t</Params>',
        '\t\t\t\t<layers>',
        slices,
        '\t\t\t\t</layers>',
        '\t\t\t\t<OutputDevice>',
        `\t\t\t\t\t<OutputDeviceVirtual name="${escapeXml(outputName)}" deviceId="${escapeXml(`Virtual${outputName}`)}" width="${screen.virtualOutput.width}" height="${screen.virtualOutput.height}"/>`,
        '\t\t\t\t</OutputDevice>',
        '\t\t\t</Screen>',
      ].join('\n');
    })
    .join('\n');

  return [
    '<?xml version="1.0" encoding="utf-8"?>',
    `<XmlState name="${escapeXml(layout.composition.name)}">`,
    `\t<versionInfo name="${RESOLUME_PROFILE.application}" majorVersion="${RESOLUME_PROFILE.majorVersion}" minorVersion="${RESOLUME_PROFILE.minorVersion}" microVersion="${RESOLUME_PROFILE.microVersion}" revision="${RESOLUME_PROFILE.revision}"/>`,
    '\t<ScreenSetup name="ScreenSetup">',
    '\t\t<Params name="ScreenSetupParams"/>',
    `\t\t<CurrentCompositionTextureSize width="${layout.composition.width}" height="${layout.composition.height}"/>`,
    '\t\t<screens>',
    screens,
    '\t\t</screens>',
    '\t</ScreenSetup>',
    '</XmlState>',
    '',
  ].join('\n');
}

export function buildResolumeReadme(layout: ResolumeLayout, presetFile: string): string {
  const outputLines = layout.screens.flatMap((screen) =>
    screen.slices.map(
      (slice) =>
        `${slice.index}. ${slice.name} — ${slice.nativeResolution.width}x${slice.nativeResolution.height}px` +
        (slice.mediaFile ? ` — ${slice.mediaFile}` : ''),
    ),
  );
  return [
    'ScreenGrid → Resolume Arena Advanced Output',
    '',
    `Preset: ${presetFile}`,
    `Compatibility profile: ${layout.compatibility.profile}`,
    `Composition: ${layout.composition.width}x${layout.composition.height}px`,
    '',
    'Import',
    '1. Set the Resolume composition to the size shown above.',
    '2. Open Output → Advanced.',
    '3. Load the XML preset from the preset dropdown, or copy it to:',
    '   Documents/Resolume Arena/Presets/Advanced Output',
    '4. Assign the single virtual Canvas Screen to the connected display or playback output.',
    '5. Confirm the Input Selection, then perform final warp and edge blending at the venue.',
    '',
    'Outputs',
    ...outputLines,
    '',
    'Notes',
    '- The preset contains one virtual Screen at the full Canvas resolution.',
    '- Every Canvas item is exported as a Slice inside that Screen.',
    '- Input Selection and Output Transformation both preserve the Canvas position and quarter-turn rotation.',
    '- Resolume does not publish the Advanced Output XML schema as a stable API; keep layout.json as a portable reference.',
    '',
  ].join('\n');
}

function buildSliceXml(
  slice: ResolumeLayout['screens'][number]['slices'][number],
  uniqueId: number,
): string {
  return [
    `\t\t\t\t\t<Slice uniqueId="${uniqueId}">`,
    '\t\t\t\t\t\t<Params name="Common">',
    `\t\t\t\t\t\t\t<Param name="Name" T="STRING" default="Layer" value="${escapeXml(slice.name)}"/>`,
    '\t\t\t\t\t\t\t<Param name="Enabled" T="BOOL" default="1" value="1"/>',
    '\t\t\t\t\t\t</Params>',
    '\t\t\t\t\t\t<Params name="Input">',
    '\t\t\t\t\t\t\t<ParamChoice name="Input Source" default="0:1" value="0:1" storeChoices="0"/>',
    '\t\t\t\t\t\t</Params>',
    '\t\t\t\t\t\t<Params name="Output">',
    '\t\t\t\t\t\t\t<Param name="Flip" T="UINT8" default="0" value="0"/>',
    '\t\t\t\t\t\t</Params>',
    ...quadXml('InputRect', slice.inputQuad, 6),
    ...quadXml('OutputRect', slice.outputQuad, 6),
    ...warperXml(slice.outputQuad, 6),
    '\t\t\t\t\t</Slice>',
  ].join('\n');
}

export function getResolumeInputQuad(item: CanvasItem): ResolumePoint[] {
  const { resolution } = getPatternMetrics(item.config);
  const { width, height } = resolution;
  if (item.rotation === 90) {
    return [
      { x: item.x + height, y: item.y },
      { x: item.x + height, y: item.y + width },
      { x: item.x, y: item.y + width },
      { x: item.x, y: item.y },
    ];
  }
  if (item.rotation === 180) {
    return [
      { x: item.x + width, y: item.y + height },
      { x: item.x, y: item.y + height },
      { x: item.x, y: item.y },
      { x: item.x + width, y: item.y },
    ];
  }
  if (item.rotation === 270) {
    return [
      { x: item.x, y: item.y + width },
      { x: item.x, y: item.y },
      { x: item.x + height, y: item.y },
      { x: item.x + height, y: item.y + width },
    ];
  }
  return rectQuad(item.x, item.y, width, height);
}

function validateResolumeCanvas(canvas: CanvasDocument): void {
  if (canvas.items.length === 0) {
    throw new Error('Add at least one Canvas item before exporting a Resolume package.');
  }
  const { outsideIds } = getCanvasWarnings(canvas);
  if (outsideIds.size > 0) {
    throw new Error('Move every item fully inside the Canvas before exporting to Resolume.');
  }
}

function rectQuad(x: number, y: number, width: number, height: number): ResolumePoint[] {
  return [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ];
}

function quadXml(name: string, quad: ResolumePoint[], level: number): string[] {
  const indent = '\t'.repeat(level);
  return [
    `${indent}<${name} orientation="0">`,
    ...quad.map(
      (point) => `${indent}\t<v x="${formatNumber(point.x)}" y="${formatNumber(point.y)}"/>`,
    ),
    `${indent}</${name}>`,
  ];
}

function warperXml(outputQuad: ResolumePoint[], level: number): string[] {
  const indent = '\t'.repeat(level);
  const [topLeft, topRight, bottomRight, bottomLeft] = outputQuad;
  if (!topLeft || !topRight || !bottomRight || !bottomLeft) {
    throw new Error('Resolume output quad must contain four points.');
  }
  const vertices: ResolumePoint[] = [];
  for (let row = 0; row < 4; row += 1) {
    const rowProgress = row / 3;
    const left = interpolatePoint(topLeft, bottomLeft, rowProgress);
    const right = interpolatePoint(topRight, bottomRight, rowProgress);
    for (let column = 0; column < 4; column += 1) {
      vertices.push(interpolatePoint(left, right, column / 3));
    }
  }
  return [
    `${indent}<Warper>`,
    `${indent}\t<Params name="Warper">`,
    `${indent}\t\t<ParamChoice name="Point Mode" default="PM_LINEAR" value="PM_LINEAR" storeChoices="0"/>`,
    `${indent}\t\t<Param name="Flip" T="UINT8" default="0" value="0"/>`,
    `${indent}\t</Params>`,
    `${indent}\t<BezierWarper controlWidth="4" controlHeight="4">`,
    `${indent}\t\t<vertices>`,
    ...vertices.map(
      (point) => `${indent}\t\t\t<v x="${formatNumber(point.x)}" y="${formatNumber(point.y)}"/>`,
    ),
    `${indent}\t\t</vertices>`,
    `${indent}\t</BezierWarper>`,
    `${indent}\t<Homography>`,
    `${indent}\t\t<src>`,
    ...outputQuad.map(
      (point) => `${indent}\t\t\t<v x="${formatNumber(point.x)}" y="${formatNumber(point.y)}"/>`,
    ),
    `${indent}\t\t</src>`,
    `${indent}\t\t<dst>`,
    ...outputQuad.map(
      (point) => `${indent}\t\t\t<v x="${formatNumber(point.x)}" y="${formatNumber(point.y)}"/>`,
    ),
    `${indent}\t\t</dst>`,
    `${indent}\t</Homography>`,
    `${indent}</Warper>`,
  ];
}

function interpolatePoint(start: ResolumePoint, end: ResolumePoint, amount: number): ResolumePoint {
  return {
    x: start.x + (end.x - start.x) * amount,
    y: start.y + (end.y - start.y) * amount,
  };
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
}

function escapeXml(value: string): string {
  return [...value]
    .filter((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return (
        codePoint === 0x09 ||
        codePoint === 0x0a ||
        codePoint === 0x0d ||
        (codePoint >= 0x20 && codePoint <= 0xd7ff) ||
        (codePoint >= 0xe000 && codePoint <= 0xfffd) ||
        (codePoint >= 0x10000 && codePoint <= 0x10ffff)
      );
    })
    .join('')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
